import {
  ResponsiveContainer,
  ComposedChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Bar,
  Line,
  Brush,
  Label,
  BarChart,
  LabelList,
} from "recharts";
import { COST_UNIT, MEGA_POWER_UNIT } from "../../../Units";
import { PrimaryColor, SecondaryColor, COLOR_CODES } from "../../../common";
import {
  UtilizationTrendElement,
  UtilizationTrendMCP,
} from "../../../store/state/BuyerSellerState";
import React, { useState, useEffect } from "react";
import { COLORS } from "../../../components/recharts/ReCharts";
import { BrushStart, ChartType } from "../../../models/chart_model";
import {
  renderHourTick,
  renderQuarterTick,
} from "../../../components/recharts/components";
import { ReMixChart } from "../../../components/recharts/OldReMixCharts";
import { RootState } from "../../../store/store";
import { useSelector } from "react-redux";
export const AxisLabel = ({
  axisType,
  x,
  y,
  width,
  height,
  stroke,
  children,
}: any) => {
  const isVert = axisType === "yAxis";
  const cx = isVert ? x : x + width / 2;
  const cy = isVert ? height / 2 + y : y + height + 10;
  const rot = isVert ? `270 ${cx} ${cy}` : 0;
  return (
    <text
      x={cx}
      y={cy}
      transform={`rotate(${rot})`}
      textAnchor="middle"
      stroke={stroke}
    >
      {children}
    </text>
  );
};

export function getTimeRange(slot: number, blockDurationMinutes = 15): string {
  const maxSlot = Math.round(24 * 60 / blockDurationMinutes);
  if (slot < 1 || slot > maxSlot) return "";

  const startMinutes = (slot - 1) * blockDurationMinutes;
  const startHour = Math.floor(startMinutes / 60);
  const startMinute = startMinutes % 60;

  const endMinutes = startMinutes + blockDurationMinutes;
  const endHour = Math.floor(endMinutes / 60);
  const endMinute = endMinutes % 60;

  const pad = (n: number) => n.toString().padStart(2, "0");
  return `${pad(startHour)}:${pad(startMinute)}-${pad(endHour)}:${pad(endMinute)}`;
}

export const ExchangeChart = ({
  showBrush = false,
  title,
  data,
  height = "25%",
  width = "100%",
  syncId = "Exchange",
  onlyBrush = false,
  brushStart = BrushStart.End,
  setShownLegends,
  onBarClick,
  shownLegnends = [],
}: {
  showBrush?: boolean;
  onlyBrush?: boolean;
  title?: string;
  data: any[];
  shownLegnends: string[];
  setShownLegends: (legends: string[]) => void;
  height?: string;
  width?: string;
  brushStart?: BrushStart;
  syncId?: string;
  onBarClick?: (e: any) => void;
}) => {
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const active = useSelector((state: RootState) => state.menu.isActive);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return (
    <>
      {!onlyBrush && (
        <h2
          className="text-center text-xl m-2 "
          style={{
            color: SecondaryColor,
          }}
        >
          {title}
        </h2>
      )}

      <ResponsiveContainer width={width} height={height}>
        <ComposedChart syncId={syncId} data={data} margin={{}}>
          {!onlyBrush && (
            <>
              <CartesianGrid strokeDasharray="4 2" />

              <XAxis
                dataKey="time_slot"
                tick={renderHourTick as any}
                interval={0}
                fontSize={12}
              />
              <XAxis
                dataKey="date"
                axisLine={false}
                tickLine={false}
                fontSize={12}
                interval={0}
                tick={renderQuarterTick as any}
                height={20}
                xAxisId="quarter"
              />
              <YAxis fontSize={12} width={37}>
                <Label
                  value={COST_UNIT}
                  fontSize={13}
                  angle={-90}
                  position="insideLeft"
                  style={{ textAnchor: "middle" }}
                />
              </YAxis>

              <YAxis
                yAxisId="right"
                fontSize={11}
                orientation="right"
                width={68}
              >
                <Label
                  value={"MW"}
                  fontSize={13}
                  angle={-90}
                  position="insideRight"
                  style={{ textAnchor: "middle" }}
                />
              </YAxis>

              <Tooltip
                labelFormatter={(value, payload) => {
                  try {
                    return [
                      `${payload[0].payload.date}, ${getTimeRange(
                        parseInt(value)
                      )} (${value})`,
                    ];
                  } catch {
                    return [value];
                  }
                }}
                formatter={(value, name, props) => {
                  let val = parseFloat(value.toString())
                    .toFixed(2)
                    .toString()
                    .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
                  if (val === "NaN") {
                    val = "";
                  }
                  if (name === "Weighted Average Price") {
                    return [val, name.toString().concat(`(${COST_UNIT})`)];
                  }
                  return [val, name];
                }}
              />

              <Bar
                dataKey="mcv_mw"
                fill={"#DEDFDF"}
                onClick={(e) => {
                  console.log("Bar Clicked", e);
                  onBarClick && onBarClick(e);
                }}
                hide={
                  !shownLegnends.includes(`MCV (${MEGA_POWER_UNIT})`) &&
                  shownLegnends.length > 0
                }
                name={`MCV (${MEGA_POWER_UNIT})`}
                yAxisId="right"
                radius={[4, 4, 0, 0]}
                isAnimationActive={false}
              />
            </>
          )}
          <Line
            dot={false}
            strokeWidth={onlyBrush ? 0 : 2}
            hide={
              !shownLegnends.includes(`Sell Bids(${MEGA_POWER_UNIT})`) &&
              shownLegnends.length > 0
            }
            yAxisId="right"
            dataKey="sell_bid_mw"
            onClick={(e) => {
              console.log("Line Clicked", e);
              onBarClick && onBarClick(e);
            }}
            stroke={SecondaryColor}
            isAnimationActive={false}
            color={SecondaryColor}
            fill={SecondaryColor}
            name={`Sell Bids(${MEGA_POWER_UNIT})`}
          />

          <Line
            dot={false}
            strokeWidth={onlyBrush ? 0 : 2}
            yAxisId="right"
            style={{
              display: onlyBrush ? "none" : "block",
            }}
            onClick={(e) => {
              console.log("Line Clicked", e);
              onBarClick && onBarClick(e);
            }}
            dataKey="purchase_bid_mw"
            stroke={"#333333"}
            isAnimationActive={true}
            color={"#333333"}
            fill={"#333333"}
            name={`Purchase Bids(${MEGA_POWER_UNIT})`}
          />
          <Line
            dot={false}
            strokeWidth={onlyBrush ? 0 : 2}
            dataKey="wt_mcp_rs_mwh"
            hide={
              !shownLegnends.includes(`Price(${COST_UNIT})`) &&
              shownLegnends.length > 0
            }
            name={`Price(${COST_UNIT})`}
            stroke={PrimaryColor}
            isAnimationActive={false}
            color={PrimaryColor}
            fill={PrimaryColor}
          />

          {showBrush ? (
            <Brush
              startIndex={
                data && data.length > 0
                  ? data.length <= 96
                    ? 0 // If data is 96 or less, start from beginning
                    : data.length - 96 // If data is more than 96, show last 96 items
                  : 0
              }
              endIndex={
                data && data.length > 0
                  ? data.length - 1 // Always end at the last item
                  : 0
              }
              dataKey="date"
              height={8}
              fillOpacity={1}
              color={PrimaryColor}
              floodColor={PrimaryColor}
              fill={"white"}
              stroke={PrimaryColor}
              width={windowWidth - (active ? 220 : 170)}
            />
          ) : (
            <Brush
              startIndex={BrushStart.Start}
              endIndex={BrushStart.End}
              dataKey="date"
              height={0}
              stroke={PrimaryColor}
            />
          )}
        </ComposedChart>
      </ResponsiveContainer>
    </>
  );
};

export const ExchangeReChart = ({
  showBrush = false,
  title,
  data,
  height = "25%",
  width = "100%",
  syncId = "Exchange",
  onlyBrush = false,
  brushStart = BrushStart.End,
  setShownLegends,
  onBarClick,
  shownLegnends = [],
}: {
  showBrush?: boolean;
  onlyBrush?: boolean;
  title?: string;
  data: any[];
  shownLegnends: string[];
  setShownLegends: (legends: string[]) => void;
  height?: string;
  width?: string;
  brushStart?: BrushStart;
  syncId?: string;
  onBarClick?: (e: any) => void;
}) => (
  <div className="flex flex-col w-full h-full">
    {!onlyBrush && (
      <h2
        className="text-center text-l font-medium m-2 "
        style={{
          color: SecondaryColor,
        }}
      >
        {title}
      </h2>
    )}

    <ReMixChart
      dynamicMarginTop="mt-[-1.5rem]"
      syncid={syncId}
      data={data}
      circleLegend={true}
      xTick={(value: any, index: any) => {
        return renderHourTick(value, 10);
      }}
      onBarClick={onBarClick}
      secondXAxisHeight={10}
      xaxisHeight={40}
      brushHeight={0}
      fontSize={10}
      yAxisWidth={48}
      secondYAxisLabel={COST_UNIT}
      yAxisLabel={MEGA_POWER_UNIT}
      showBrush={true}
      brushIndex={{
        startIndex:
          data && data.length > 0
            ? data.length <= 96
              ? 0 // If data is 96 or less, start from beginning
              : data.length - 96 // If data is more than 96, show last 96 items
            : 0,
        endIndex:
          data && data.length > 0
            ? data.length - 1 // Always end at the last item
            : 0,
      }}
      unit="MW"
      legends={[
        {
          name: "MCV(MW)",
          stroke: COLOR_CODES.monthlyPrice,
          dataKey: "mcv_mw",
          fill: COLOR_CODES.monthlyPrice,
          type: ChartType.Bar,
        },
        {
          name: "Sell Bids(MW)",
          stroke: COLOR_CODES.sellBid,
          dataKey: "sell_bid_mw",
        },
        {
          name: "Purchase Bids(MW)",
          stroke: COLOR_CODES.purchaseBids,
          dataKey: "purchase_bid_mw",
        },
        {
          name: "Price(MWh)",
          stroke: COLOR_CODES.primary,
          dataKey: "wt_mcp_rs_mwh",
          type: ChartType.Line,
          yAxisId: "right",
        },
      ]}
      xDataKey="time_slot"
      secondXDataKey="date"
    />
  </div>
);

export const BuyerSellerChart = ({
  data,
  showLegend,
}: {
  data: any[];
  showLegend: boolean;
}) => {
  return (
    <ResponsiveContainer>
      <BarChart
        layout="vertical"
        barCategoryGap={2}
        barGap={2}
        data={data}

        // syncId={"buyerSeller"}
      >
        <YAxis
          height={0}
          minTickGap={10}
          axisLine={false}
          tickLine={false}
          allowDataOverflow={false}
          fontSize={13}
          width={160}
          dataKey="name"
          type="category"
        ></YAxis>
        <XAxis
          width={0}
          height={0}
          type="number"
          // tickFormatter={(value) => {
          //   return "";

          // }}
        >
          <Label
            // value="MWh"
            angle={-90}
            position="left"
            style={{ textAnchor: "middle" }}
          />
        </XAxis>
        <Tooltip
          formatter={(value, name, props) => {
            return [
              parseFloat(value.toString())
                .toFixed(2)
                .toString()
                .replace(/\B(?=(\d{3})+(?!\d))/g, ",") + " MWh",
            ];
          }}
          contentStyle={{ backgroundColor: "#fff" }} // Optional: Customize tooltip background color
          itemStyle={{ color: "#333333" }} // Set text color to black
        />

        {/* <Legend verticalAlign="top" /> */}

        <Bar
          radius={4}
          dataKey={"value"}
          barSize={40}
          color={"#333333"}
          fill={"#F1F1F1"}
        >
          <LabelList
            fontSize={11}
            fill={"#333333"}
            fontWeight={500}
            formatter={(value: any) => {
              return (
                (parseFloat(value) / 1000)
                  .toFixed(2)
                  .toString()
                  .replace(/\B(?=(\d{3})+(?!\d))/g, ",") + "k MWh"
              );
            }}
            dataKey="value"
            position="insideLeft"
          ></LabelList>
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
};

const RADIAN = Math.PI / 180;
export const renderCustomizedLabel = ({
  cx,
  cy,
  midAngle,
  innerRadius,
  outerRadius,
  percent,
  index,
}: {
  cx: number;
  cy: number;
  midAngle: number;
  innerRadius: number;
  outerRadius: number;
  percent: number;
  index: number;
}) => {
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return (
    <text
      x={x}
      y={y}
      fill="white"
      textAnchor={x > cx ? "start" : "end"}
      dominantBaseline="central"
    >
      {` ${percent > 0.15 ? (percent * 100).toFixed(0) + "%" : ""}`}
    </text>
  );
};

export const UtilizationTrendChart = ({
  data,
  mcp,
  legends,
}: {
  data: UtilizationTrendElement[];
  mcp: UtilizationTrendMCP[];
  legends: { name: string }[];
}) => {
  return (
    <ResponsiveContainer>
      <ComposedChart
        syncId={"utilizationChart"}
        data={
          // Add the mcp data to the data

          data.map((element, index) => {
            let mcpElement = mcp[index];
            if (mcpElement) {
              return {
                ...element,
                wt_mcp: mcpElement.wt_mcp,
              };
            }
            return element;
          })
        }
      >
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="name" />
        <YAxis fontSize={13} width={37}>
          <Label
            value={COST_UNIT}
            fontSize={15}
            angle={-90}
            position="insideLeft"
            style={{ textAnchor: "middle" }}
          />
        </YAxis>
        {/* <YAxis name="MW" label={"MW"} width={0} /> */}

        <YAxis yAxisId="right" fontSize={13} orientation="right" width={68}>
          <Label
            value={"MWh"}
            fontSize={15}
            angle={-90}
            position="insideRight"
            style={{ textAnchor: "middle" }}
          />
        </YAxis>

        <YAxis yAxisId="right" orientation="right" />
        <Tooltip
          formatter={(value, name, props) => {
            if (name === "wt_mcp") {
              return [
                parseFloat(value.toString())
                  .toFixed(2)
                  .toString()
                  .replace(/\B(?=(\d{3})+(?!\d))/g, ",") + " MWh",
              ];
            }
            return [
              name +
                " : " +
                parseFloat(value.toString())
                  .toFixed(2)
                  .toString()
                  .replace(/\B(?=(\d{3})+(?!\d))/g, ",") +
                " MWh",
            ];
          }}
        />

        <Bar
          dataKey="wt_mcp"
          fill={"#DEDFDF"}
          name={`MCP (${COST_UNIT})`}
          radius={[4, 4, 0, 0]}
          maxBarSize={30}
        />
        {legends.map((legend, index) => {
          return (
            <Line
              yAxisId="right"
              key={index}
              type="monotone"
              dataKey={legend.name}
              stroke={
                COLORS[
                  index > COLORS.length - 1 ? index - COLORS.length : index
                ]
              }
              dot={false}
              strokeWidth={2}
            />
          );
        })}
      </ComposedChart>
    </ResponsiveContainer>
  );
};
